import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BadgeIcon from "@mui/icons-material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";

import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { API_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const normalizeCodeArray = (arr = [], keys = []) =>
  (arr || [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return String(item);
      }

      for (const key of keys) {
        const value = item?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          return String(value);
        }
      }

      const fallback =
        item?.v ??
        item?.value ??
        item?.code ??
        item?.label ??
        "";

      return fallback === "" ? null : String(fallback);
    })
    .filter(Boolean);

const makeBaseRow = (campusCode = "") => ({
  ID: null,
  CampusCode: campusCode,
  FromDate: "",
  ToDate: "",
  IsActive: 1,
});

const ROLE_CONFIGS = [
  {
    key: "dean",
    label: "Dean",
    title: "Dean",
    description: "Assign and manage Dean responsibilities by sub-campus.",
    icon: <SchoolIcon />,
    endpoint: "deans",
    uploadEndpoint: "UploadDeans",
    personType: "employee",
    personIdField: "EmployeeID",
    personNameField: "Name",
    filters: ["SubCampusCode", "People"],
    fields: ["SubCampusCode", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["SubCampusCode", "Name", "EmployeeID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      SubCampusCode: "",
      Name: "",
      EmployeeID: null,
    }),
    sampleSheet: "Deans",
    sampleFile: "Deans_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        SubCampusCode: "SC01",
        DeanEmployeeID: "DEAN101",
        Name: "Sample Dean",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
  {
    key: "hod",
    label: "HOD",
    title: "HOD",
    description: "Assign HOD responsibilities by sub-campus, degree, branch and semester.",
    icon: <AccountTreeIcon />,
    endpoint: "hods",
    uploadEndpoint: "UploadHOD",
    personType: "employee",
    personIdField: "EmployeeID",
    personNameField: "Name",
    filters: ["SubCampusCode", "DegreeCode", "BranchCode", "SemesterCode", "People"],
    fields: ["SubCampusCode", "DegreeCode", "BranchCode", "SemesterCode", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["SubCampusCode", "DegreeCode", "BranchCode", "SemesterCode", "Name", "EmployeeID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      SubCampusCode: "",
      DegreeCode: "",
      BranchCode: "",
      SemesterCode: "",
      Name: "",
      EmployeeID: null,
    }),
    sampleSheet: "HOD",
    sampleFile: "HOD_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        SubCampusCode: "SC01",
        DegreeCode: "BTECH",
        BranchCode: "CSE",
        SemesterCode: "11",
        HODEmployeeID: "HOD101",
        Name: "Sample HOD",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
  {
    key: "classTeacher",
    label: "Class Teacher",
    title: "Class Teacher",
    description: "Assign class teachers to academic class combinations.",
    icon: <GroupsIcon />,
    endpoint: "ClassTeachers",
    uploadEndpoint: "UploadClassTeachers",
    bulkStatusEndpoint: "ClassTeachers/bulk-status",
    personType: "employee",
    personIdField: "EmployeeID",
    personNameField: "Name",
    filters: ["SubCampusCode", "DegreeCode", "BranchCode", "Semester", "Section", "People"],
    fields: ["SubCampusCode", "DegreeCode", "BranchCode", "Semester", "Section", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["SubCampusCode", "DegreeCode", "BranchCode", "Semester", "Section", "Name", "EmployeeID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      SubCampusCode: "",
      DegreeCode: "",
      BranchCode: "",
      Semester: "",
      Section: "",
      Name: "",
      EmployeeID: null,
    }),
    sampleSheet: "ClassTeachers",
    sampleFile: "ClassTeachers_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        SubCampusCode: "SC01",
        DegreeCode: "BTECH",
        BranchCode: "CSM",
        Semester: "31",
        Section: "2",
        EmployeeID: "KIET301",
        Name: "Sample Teacher",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
  {
    key: "teacher",
    label: "Teacher",
    title: "Teacher",
    description: "Assign and manage Teacher role access by sub-campus.",
    icon: <PersonIcon />,
    endpoint: "RoleTeachers",
    uploadEndpoint: "UploadRoleTeachers",
    personType: "employee",
    personIdField: "EmployeeID",
    personNameField: "Name",
    filters: ["SubCampusCode", "People"],
    fields: ["SubCampusCode", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["SubCampusCode", "Name", "EmployeeID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      SubCampusCode: "",
      Name: "",
      EmployeeID: null,
    }),
    sampleSheet: "Teachers",
    sampleFile: "Teachers_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        SubCampusCode: "SC01",
        EmployeeID: "TEAC101",
        Name: "Sample Teacher",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
  {
    key: "classRepresentative",
    label: "Class Representative",
    title: "Class Representative",
    description: "Assign student class representatives by sub-campus.",
    icon: <BadgeIcon />,
    endpoint: "RoleClassRepresentative",
    uploadEndpoint: "UploadRoleClassRepresentative",
    personType: "student",
    personIdField: "StudentID",
    personKeyField: "StudentKey",
    personNameField: "Name",
    filters: ["SubCampusCode", "People"],
    fields: ["SubCampusCode", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["SubCampusCode", "Name", "StudentID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      SubCampusCode: "",
      Name: "",
      StudentKey: null,
      StudentID: "",
    }),
    sampleSheet: "ClassRepresentative",
    sampleFile: "RoleClassRepresentative_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        SubCampusCode: "SC01",
        StudentID: "26Q1A4201",
        Name: "Sample Class Representative",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
  {
    key: "hostelManager",
    label: "Hostel Manager",
    title: "Hostel Manager",
    description: "Assign hostel managers by hostel block.",
    icon: <HomeWorkIcon />,
    endpoint: "RoleHostelManagers",
    uploadEndpoint: "UploadRoleHostelManagers",
    personType: "employee",
    personIdField: "EmployeeID",
    personNameField: "Name",
    hostel: true,
    filters: ["Block", "People"],
    fields: ["Block", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["Block", "Name", "EmployeeID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      Block: "",
      Name: "",
      EmployeeID: null,
    }),
    sampleSheet: "HostelManagers",
    sampleFile: "HostelManagers_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        Block: "Block A",
        EmployeeID: "HM101",
        Name: "Sample Hostel Manager",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
  {
    key: "hostelViewer",
    label: "Hostel Viewer",
    title: "Hostel Viewer",
    description: "Assign hostel viewers by block and floor.",
    icon: <VisibilityIcon />,
    endpoint: "RoleHostelViewers",
    uploadEndpoint: "UploadRoleHostelViewers",
    personType: "employee",
    personIdField: "EmployeeID",
    personNameField: "Name",
    hostel: true,
    filters: ["Block", "FloorNumber", "People"],
    fields: ["Block", "FloorNumber", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["Block", "FloorNumber", "Name", "EmployeeID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      Block: "",
      FloorNumber: "",
      Name: "",
      EmployeeID: null,
    }),
    sampleSheet: "HostelViewers",
    sampleFile: "HostelViewers_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        Block: "Block A",
        FloorNumber: "1",
        EmployeeID: "HV101",
        Name: "Sample Hostel Viewer",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
  {
    key: "hostelRepresentative",
    label: "Hostel Representative",
    title: "Hostel Representative",
    description: "Assign student hostel representatives by block, floor and room.",
    icon: <ApartmentIcon />,
    endpoint: "ManageHostelRepresentatives",
    uploadEndpoint: "UploadHostelRepresentatives",
    personType: "hostelStudent",
    personIdField: "StudentID",
    personKeyField: "StudentKey",
    personNameField: "RepresentativeName",
    hostel: true,
    filters: ["Block", "FloorNumber", "RoomNumber", "People"],
    fields: ["Block", "FloorNumber", "RoomNumber", "Person", "FromDate", "ToDate", "IsActive"],
    columns: ["Block", "FloorNumber", "RoomNumber", "RepresentativeName", "StudentID", "FromDate", "ToDate", "IsActive"],
    initial: (cc) => ({
      ...makeBaseRow(cc),
      Block: "",
      FloorNumber: "",
      RoomNumber: "",
      StudentKey: null,
      StudentID: "",
      RepresentativeName: "",
    }),
    sampleSheet: "HostelRepresentatives",
    sampleFile: "HostelRepresentatives_Sample",
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        Block: "Block A",
        FloorNumber: "1",
        RoomNumber: "101",
        StudentID: "226Q1A4201",
        FromDate: "2026-01-01",
        ToDate: "2026-06-30",
        IsActive: 1,
      },
    ],
  },
];

const createRoleMap = (factory) =>
  Object.fromEntries(ROLE_CONFIGS.map((cfg) => [cfg.key, factory(cfg)]));

export default function RoleAssignment() {
  // Exact SetupAcademicCode theme family.
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [tab, setTab] = useState(0);
  const activeConfig = ROLE_CONFIGS[tab];

  const [campusCode, setCampusCode] = useState("");
  const [dataByRole, setDataByRole] = useState(createRoleMap(() => []));
  const [filtersByRole, setFiltersByRole] = useState(
    createRoleMap((cfg) =>
      Object.fromEntries((cfg.filters || []).map((key) => [key, []]))
    )
  );
  const [selectedByRole, setSelectedByRole] = useState(createRoleMap(() => []));
  const [pageByRole, setPageByRole] = useState(createRoleMap(() => 0));
  const [rowsPerPageByRole, setRowsPerPageByRole] = useState(
    createRoleMap(() => 25)
  );

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(activeConfig.initial(""));
  const [saving, setSaving] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [academicLookup, setAcademicLookup] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    Semester: [],
    Section: [],
  });

  const [cascadeLookup, setCascadeLookup] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    Semester: [],
    Section: [],
  });

  const [hostelLookup, setHostelLookup] = useState({
    blocks: [],
    floors: [],
    rooms: [],
  });

  const [personOptions, setPersonOptions] = useState([]);
  const [personLoading, setPersonLoading] = useState(false);
  const personCancelRef = useRef(null);

  const [msg, setMsg] = useState({ type: "success", text: "" });
  const fileInputs = useRef({});

  const pageWrapSx = {
    p: 2,
    backgroundColor: BG,
    minHeight: "100vh",
  };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg,${THEME_ORANGE},${THEME_ORANGE_2})`,
    },
  };
  const popupSectionSx = {
    ...surfaceSx,
    p: 2.5,
    borderRadius: 2.5,
    borderTop: `4px solid ${THEME_ORANGE}`,
    overflow: "hidden",
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    backgroundColor: "white",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const iconBtnSx = {
    color: BLUE,
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };
  const dangerIconBtnSx = {
    color: "rgba(244,67,54,0.90)",
    "&:hover": {
      backgroundColor: "rgba(244,67,54,0.08)",
      color: "rgba(244,67,54,1)",
    },
  };
  const checkboxSx = {
    color: BLUE,
    "&.Mui-checked": { color: THEME_ORANGE },
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      whiteSpace: "nowrap",
      borderBottom: "1px solid rgba(255,255,255,0.18)",
    },
  };
  const zebraRowSx = (index) => ({
    backgroundColor: index % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      borderBottom: "1px solid rgba(25,118,210,0.10)",
    },
  });
  const tabsSx = {
    px: 1,
    borderBottom: `1px solid ${BORDER}`,
    minHeight: 48,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 800,
      minHeight: 48,
    },
    "& .Mui-selected": {
      color: `${THEME_ORANGE} !important`,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: THEME_ORANGE,
      height: 3,
    },
  };

  const serialBadge = (number) => (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 900,
        fontSize: "1rem",
        color: "#1976D2",
        backgroundColor: "rgba(25,118,210,0.06)",
        border: "2px solid rgba(25,118,210,0.20)",
        lineHeight: 1,
      }}
    >
      {number}
    </Box>
  );

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const cc = decoded.campusCode || decoded.CampusCode || "";
      setCampusCode(cc);
      setFormData(activeConfig.initial(cc));
    } catch {
      setMsg({ type: "error", text: "Failed to decode authentication token." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const normalizeRoleRows = (cfg, rows) =>
    (rows || []).map((r) => {
      const base = {
        ...cfg.initial(campusCode),
        ...r,
        ID: r.ID ?? r.Id ?? r.id,
        CampusCode: r.CampusCode ?? r.campuscode ?? campusCode,
        FromDate: String(
          r.FromDate || r.StartDate || r.startDate || ""
        ).slice(0, 10),
        ToDate: String(
          r.ToDate || r.EndDate || r.endDate || ""
        ).slice(0, 10),
        IsActive: Number(r.IsActive ?? r.isActive ?? 1),
      };

      if ("SubCampusCode" in base) {
        base.SubCampusCode =
          r.SubCampusCode ?? r.subCampusCode ?? base.SubCampusCode ?? "";
      }
      if ("DegreeCode" in base) {
        base.DegreeCode = String(
          r.DegreeCode ?? r.degreeCode ?? base.DegreeCode ?? ""
        );
      }
      if ("BranchCode" in base) {
        base.BranchCode = String(
          r.BranchCode ?? r.branchCode ?? base.BranchCode ?? ""
        );
      }
      if ("SemesterCode" in base) {
        base.SemesterCode = String(
          r.SemesterCode ?? r.semesterCode ?? base.SemesterCode ?? ""
        );
      }
      if ("Semester" in base) {
        base.Semester = String(
          r.Semester ?? r.semester ?? base.Semester ?? ""
        );
      }
      if ("Section" in base) {
        base.Section = String(r.Section ?? r.section ?? base.Section ?? "");
      }
      if ("Block" in base) {
        base.Block = r.Block ?? r.HostelBlock ?? r.block ?? base.Block ?? "";
      }
      if ("FloorNumber" in base) {
        base.FloorNumber = String(
          r.FloorNumber ??
            r.Floor ??
            r.HostelFloor ??
            r.floorNumber ??
            r.floor ??
            base.FloorNumber ??
            ""
        );
      }
      if ("RoomNumber" in base) {
        base.RoomNumber = String(
          r.RoomNumber ?? r.roomNumber ?? base.RoomNumber ?? ""
        );
      }
      if ("EmployeeID" in base) {
        base.EmployeeID =
          r.EmployeeID ?? r.employeeId ?? base.EmployeeID ?? null;
        base.Name =
          r.Name ??
          r.EmployeeName ??
          r.fullName ??
          r.FullName ??
          base.Name ??
          "";
      }
      if (cfg.personType === "student") {
        base.StudentKey = r.StudentKey ?? r.studentKey ?? null;
        base.StudentID = String(r.StudentID ?? r.studentId ?? "");
        base.Name =
          r.Name ??
          r.StudentName ??
          r.fullName ??
          r.FullName ??
          base.Name ??
          "";
      }
      if (cfg.personType === "hostelStudent") {
        base.StudentKey = r.StudentKey ?? r.studentKey ?? null;
        base.StudentID = String(r.StudentID ?? r.studentId ?? "");
        base.RepresentativeName =
          r.RepresentativeName ??
          r.Name ??
          r.StudentName ??
          base.RepresentativeName ??
          "";
      }

      return base;
    });

  const fetchRows = async (cfg = activeConfig, { silent = false } = {}) => {
    if (!campusCode) return;
    if (!silent) setLoadingRows(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/${cfg.endpoint}`,
        authHeader
      );
      setDataByRole((prev) => ({
        ...prev,
        [cfg.key]: normalizeRoleRows(cfg, res.data || []),
      }));
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          `Failed to fetch ${cfg.label} assignments.`,
      });
    } finally {
      if (!silent) setLoadingRows(false);
    }
  };

  useEffect(() => {
    if (!campusCode) return;
    fetchRows(activeConfig);
    setSelectedByRole((prev) => ({ ...prev, [activeConfig.key]: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode, tab]);

  const fetchCommonAcademicLookups = async () => {
    if (!campusCode) return;
    setLoadingLookups(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
        ...authHeader,
        params: { CampusCode: campusCode },
      });

      setAcademicLookup({
        SubCampusCode: normalizeCodeArray(data?.SubCampusCode || [], [
          "SubCampusCode",
        ]),
        DegreeCode: normalizeCodeArray(data?.DegreeCode || [], ["DegreeCode"]),
        BranchCode: normalizeCodeArray(data?.BranchCode || [], ["BranchCode"]),
        SemesterCode: normalizeCodeArray(
          data?.SemesterCode || data?.Semester || [],
          ["SemesterCode", "Semester"]
        ),
        Semester: normalizeCodeArray(
          data?.SemesterCode || data?.Semester || [],
          ["SemesterCode", "Semester"]
        ),
        Section: normalizeCodeArray(data?.Section || [], ["Section"]),
      });
    } catch (e) {
      console.error("RoleAssignment academic lookup error:", e);
    } finally {
      setLoadingLookups(false);
    }
  };

  const fetchCascadeBase = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/cascadelookups/all`,
        {
          ...authHeader,
          params: { campusCode },
        }
      );

      setCascadeLookup((prev) => ({
        ...prev,
        SubCampusCode: normalizeCodeArray(data?.SubCampusCode || [], [
          "SubCampusCode",
        ]),
        SemesterCode: normalizeCodeArray(
          data?.SemesterCode || data?.Semester || [],
          ["SemesterCode", "Semester"]
        ),
        Semester: normalizeCodeArray(
          data?.SemesterCode || data?.Semester || [],
          ["SemesterCode", "Semester"]
        ),
        Section: normalizeCodeArray(data?.Section || [], ["Section"]),
      }));
    } catch (e) {
      console.error("Cascade base lookup error:", e);
    }
  };

  const fetchCascadeDegrees = async (subCampusCode) => {
    if (!subCampusCode) {
      setCascadeLookup((prev) => ({
        ...prev,
        DegreeCode: [],
        BranchCode: [],
      }));
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/api/cascadelookups/all`,
        {
          ...authHeader,
          params: { campusCode, subCampusCode },
        }
      );

      setCascadeLookup((prev) => ({
        ...prev,
        DegreeCode: normalizeCodeArray(data?.DegreeCode || [], [
          "DegreeCode",
        ]),
        BranchCode: [],
      }));
    } catch (e) {
      console.error("Degree cascade lookup error:", e);
    }
  };

  const fetchCascadeBranches = async (subCampusCode, degreeCode) => {
    if (!subCampusCode || !degreeCode) {
      setCascadeLookup((prev) => ({ ...prev, BranchCode: [] }));
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/api/cascadelookups/all`,
        {
          ...authHeader,
          params: { campusCode, subCampusCode, degreeCode },
        }
      );

      setCascadeLookup((prev) => ({
        ...prev,
        BranchCode: normalizeCodeArray(data?.BranchCode || [], [
          "BranchCode",
        ]),
      }));
    } catch (e) {
      console.error("Branch cascade lookup error:", e);
    }
  };

  const fetchHostelLookups = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/HostelRoleLookups`,
        authHeader
      );

      setHostelLookup({
        blocks: normalizeCodeArray(data?.blocks || [], ["Block"]),
        floors: normalizeCodeArray(data?.floors || [], ["FloorNumber"]),
        rooms: Array.isArray(data?.rooms) ? data.rooms : [],
      });
    } catch (e) {
      console.error("Hostel role lookup error:", e);
      setMsg({
        type: "error",
        text: "Failed to load hostel role lookups.",
      });
    }
  };

  useEffect(() => {
    if (!campusCode) return;
    fetchCommonAcademicLookups();
    fetchCascadeBase();
    fetchHostelLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode]);

  const searchPeople = async (query, cfg = activeConfig) => {
    const q = String(query || "").trim();
    if (q.length < 2) return;

    if (personCancelRef.current) {
      personCancelRef.current.cancel("New role assignment search started");
    }
    personCancelRef.current = axios.CancelToken.source();
    setPersonLoading(true);

    try {
      if (cfg.personType === "employee") {
        const res = await axios.get(`${API_URL}/api/Employees/search`, {
          params: { q },
          ...authHeader,
          cancelToken: personCancelRef.current.token,
        });
        const arr = res.data?.employees || res.data || [];
        setPersonOptions(
          arr.map((item) => ({
            key: String(item.EmployeeID ?? item.id ?? ""),
            id: String(item.EmployeeID ?? item.id ?? ""),
            name:
              item.EmployeeName ??
              item.Name ??
              item.name ??
              "",
            label: `${
              item.EmployeeName ?? item.Name ?? item.name ?? "Employee"
            } (${item.EmployeeID ?? item.id ?? ""})`,
          }))
        );
      } else if (cfg.personType === "student") {
        const res = await axios.get(`${API_URL}/api/Students/search`, {
          params: { q },
          ...authHeader,
          cancelToken: personCancelRef.current.token,
        });
        const arr = res.data?.students || res.data || [];
        setPersonOptions(
          arr.map((item) => ({
            key: String(item.StudentKey ?? item.skey ?? ""),
            studentKey: item.StudentKey ?? item.skey ?? null,
            id: String(item.StudentID ?? item.sid ?? ""),
            studentId: String(item.StudentID ?? item.sid ?? ""),
            name:
              item.StudentName ??
              item.Name ??
              item.name ??
              "",
            label: `${
              item.StudentName ?? item.Name ?? item.name ?? "Student"
            } (${item.StudentID ?? item.sid ?? ""})`,
          }))
        );
      } else {
        const res = await axios.get(`${API_URL}/api/studentList`, {
          params: { search: q },
          ...authHeader,
          cancelToken: personCancelRef.current.token,
        });
        const arr = res.data?.students || res.data || [];
        setPersonOptions(
          arr
            .map((student) => {
              const studentKey =
                student.StudentKey ??
                student.Studentkey ??
                student.studentKey ??
                student.studentkey ??
                student.student_key ??
                student.STUDENTKEY ??
                null;
              const studentId =
                student.StudentID ??
                student.studentId ??
                student.studentid ??
                student.student_id ??
                student.ID ??
                student.id ??
                "";
              const name =
                student.FullName ??
                student.StudentName ??
                student.Name ??
                student.name ??
                student.fullName ??
                "";

              return {
                key:
                  studentKey != null && studentKey !== ""
                    ? String(studentKey)
                    : String(studentId),
                studentKey:
                  studentKey != null && studentKey !== ""
                    ? String(studentKey)
                    : null,
                id: String(studentId),
                studentId: String(studentId),
                name,
                label: `${name || "Student"} (${
                  studentId || studentKey || ""
                })`,
              };
            })
            .filter((x) => x.id || x.studentKey)
        );
      }
    } catch (e) {
      if (!axios.isCancel(e)) {
        console.error("Role person search error:", e);
      }
    } finally {
      setPersonLoading(false);
    }
  };

  const resolveHostelStudentKey = async (studentId) => {
    const id = String(studentId || "").trim();
    if (!id) return null;
    const res = await axios.get(
      `${API_URL}/api/HostelRepresentativeStudent`,
      {
        params: { studentId: id },
        ...authHeader,
      }
    );
    return res.data?.StudentKey ?? res.data?.studentKey ?? null;
  };

  const norm = (value) => String(value ?? "").trim().toLowerCase();
  const valueMatches = (selected, value) =>
    !selected?.length ||
    selected.some((entry) => {
      if (typeof entry === "object") {
        const objectValue =
          entry.id ??
          entry.studentId ??
          entry.studentKey ??
          entry.name ??
          entry.label;
        return norm(objectValue) === norm(value);
      }
      return norm(entry) === norm(value);
    });

  const filteredRows = useMemo(() => {
    const cfg = activeConfig;
    const rows = dataByRole[cfg.key] || [];
    const filters = filtersByRole[cfg.key] || {};

    return rows.filter((row) => {
      for (const filterKey of cfg.filters || []) {
        const selected = filters[filterKey] || [];
        if (!selected.length) continue;

        if (filterKey === "People") {
          const personMatches = selected.some((person) => {
            const ids = [
              person?.id,
              person?.studentId,
              person?.studentKey,
              person?.name,
            ].filter(Boolean);

            const rowValues = [
              row.EmployeeID,
              row.StudentID,
              row.StudentKey,
              row.Name,
              row.RepresentativeName,
            ].filter(Boolean);

            return ids.some((id) =>
              rowValues.some((rowValue) => norm(id) === norm(rowValue))
            );
          });

          if (!personMatches) return false;
        } else if (!valueMatches(selected, row[filterKey])) {
          return false;
        }
      }

      return true;
    });
  }, [activeConfig, dataByRole, filtersByRole]);

  const currentPage = pageByRole[activeConfig.key] || 0;
  const currentRowsPerPage =
    rowsPerPageByRole[activeConfig.key] || 25;
  const maxPage = Math.max(
    0,
    Math.ceil(filteredRows.length / currentRowsPerPage) - 1
  );
  const safePage = Math.min(currentPage, maxPage);
  const paginatedRows = filteredRows.slice(
    safePage * currentRowsPerPage,
    safePage * currentRowsPerPage + currentRowsPerPage
  );

  useEffect(() => {
    if (currentPage > maxPage) {
      setPageByRole((prev) => ({
        ...prev,
        [activeConfig.key]: maxPage,
      }));
    }
  }, [activeConfig.key, currentPage, maxPage]);

  const getFilterOptions = (field) => {
    const rows = dataByRole[activeConfig.key] || [];
    const fromRows = [
      ...new Set(
        rows
          .map((row) => row[field])
          .filter(
            (value) =>
              value !== undefined &&
              value !== null &&
              String(value).trim() !== ""
          )
          .map(String)
      ),
    ];

    if (field === "SubCampusCode") {
      return [
        ...new Set([
          ...academicLookup.SubCampusCode,
          ...cascadeLookup.SubCampusCode,
          ...fromRows,
        ]),
      ];
    }
    if (field === "DegreeCode") {
      return [
        ...new Set([...academicLookup.DegreeCode, ...fromRows]),
      ];
    }
    if (field === "BranchCode") {
      return [
        ...new Set([...academicLookup.BranchCode, ...fromRows]),
      ];
    }
    if (field === "SemesterCode") {
      return [
        ...new Set([...academicLookup.SemesterCode, ...fromRows]),
      ];
    }
    if (field === "Semester") {
      return [
        ...new Set([...academicLookup.Semester, ...fromRows]),
      ];
    }
    if (field === "Section") {
      return [...new Set([...academicLookup.Section, ...fromRows])];
    }
    if (field === "Block") {
      return [...new Set([...hostelLookup.blocks, ...fromRows])];
    }
    if (field === "FloorNumber") {
      const blockFilters =
        filtersByRole[activeConfig.key]?.Block || [];
      const scoped = (hostelLookup.rooms || [])
        .filter(
          (room) =>
            !blockFilters.length ||
            blockFilters.some(
              (block) => norm(block) === norm(room.Block)
            )
        )
        .map((room) => room.FloorNumber)
        .filter(Boolean)
        .map(String);

      return [
        ...new Set([...hostelLookup.floors, ...scoped, ...fromRows]),
      ];
    }
    if (field === "RoomNumber") {
      const roleFilters = filtersByRole[activeConfig.key] || {};
      const scoped = (hostelLookup.rooms || [])
        .filter((room) => {
          const blockOk =
            !roleFilters.Block?.length ||
            roleFilters.Block.some(
              (block) => norm(block) === norm(room.Block)
            );
          const floorOk =
            !roleFilters.FloorNumber?.length ||
            roleFilters.FloorNumber.some(
              (floor) =>
                norm(floor) === norm(room.FloorNumber)
            );
          return blockOk && floorOk;
        })
        .map((room) => room.RoomNumber)
        .filter(Boolean)
        .map(String);

      return [...new Set([...scoped, ...fromRows])];
    }

    return fromRows;
  };

  const updateFilter = (key, value) => {
    setFiltersByRole((prev) => ({
      ...prev,
      [activeConfig.key]: {
        ...prev[activeConfig.key],
        [key]: value,
      },
    }));
    setPageByRole((prev) => ({
      ...prev,
      [activeConfig.key]: 0,
    }));
  };

  const clearFilters = () => {
    setFiltersByRole((prev) => ({
      ...prev,
      [activeConfig.key]: Object.fromEntries(
        (activeConfig.filters || []).map((key) => [key, []])
      ),
    }));
    setPageByRole((prev) => ({
      ...prev,
      [activeConfig.key]: 0,
    }));
  };

  const openAddForm = async () => {
    setFormMode("add");
    setFormData(activeConfig.initial(campusCode));
    setPersonOptions([]);
    await Promise.all([
      fetchCascadeBase(),
      activeConfig.hostel ? fetchHostelLookups() : Promise.resolve(),
    ]);
    setFormOpen(true);
  };

  const openEditForm = async (row) => {
    setFormMode("edit");
    setFormData({
      ...activeConfig.initial(campusCode),
      ...row,
    });

    if (activeConfig.personType === "employee" && row.EmployeeID) {
      setPersonOptions([
        {
          key: String(row.EmployeeID),
          id: String(row.EmployeeID),
          name: row.Name || "",
          label: `${row.Name || "Employee"} (${row.EmployeeID})`,
        },
      ]);
    } else if (
      activeConfig.personType === "student" &&
      (row.StudentID || row.StudentKey)
    ) {
      setPersonOptions([
        {
          key: String(row.StudentKey || row.StudentID),
          studentKey: row.StudentKey,
          id: String(row.StudentID || ""),
          studentId: String(row.StudentID || ""),
          name: row.Name || "",
          label: `${row.Name || "Student"} (${
            row.StudentID || row.StudentKey
          })`,
        },
      ]);
    } else if (
      activeConfig.personType === "hostelStudent" &&
      (row.StudentID || row.StudentKey)
    ) {
      setPersonOptions([
        {
          key: String(row.StudentKey || row.StudentID),
          studentKey: row.StudentKey,
          id: String(row.StudentID || ""),
          studentId: String(row.StudentID || ""),
          name: row.RepresentativeName || "",
          label: `${row.RepresentativeName || "Student"} (${
            row.StudentID || row.StudentKey
          })`,
        },
      ]);
    } else {
      setPersonOptions([]);
    }

    await fetchCascadeBase();
    if (row.SubCampusCode) {
      await fetchCascadeDegrees(row.SubCampusCode);
    }
    if (row.SubCampusCode && row.DegreeCode) {
      await fetchCascadeBranches(
        row.SubCampusCode,
        row.DegreeCode
      );
    }
    if (activeConfig.hostel) {
      await fetchHostelLookups();
    }

    setFormOpen(true);
  };

  const updateFormField = async (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "SubCampusCode") {
        if ("DegreeCode" in next) next.DegreeCode = "";
        if ("BranchCode" in next) next.BranchCode = "";
      }
      if (field === "DegreeCode") {
        if ("BranchCode" in next) next.BranchCode = "";
      }
      if (field === "Block") {
        if ("FloorNumber" in next) next.FloorNumber = "";
        if ("RoomNumber" in next) next.RoomNumber = "";
      }
      if (field === "FloorNumber") {
        if ("RoomNumber" in next) next.RoomNumber = "";
      }

      return next;
    });

    if (field === "SubCampusCode") {
      await fetchCascadeDegrees(value);
    }
    if (field === "DegreeCode") {
      await fetchCascadeBranches(
        formData.SubCampusCode,
        value
      );
    }
  };

  const buildPayload = (cfg, row) => {
    const payload = {
      CampusCode: campusCode,
      FromDate: row.FromDate || null,
      ToDate: row.ToDate || null,
      IsActive: row.IsActive ? 1 : 0,
    };

    [
      "SubCampusCode",
      "DegreeCode",
      "BranchCode",
      "SemesterCode",
      "Semester",
      "Section",
      "Block",
      "FloorNumber",
      "RoomNumber",
    ].forEach((key) => {
      if (key in row) {
        payload[key] = row[key] || null;
      }
    });

    if (cfg.personType === "employee") {
      payload.EmployeeID = row.EmployeeID || null;
      payload.Name = row.Name || "";
    } else if (cfg.personType === "student") {
      payload.StudentKey = row.StudentKey || null;
      payload.StudentID = row.StudentID || null;
      payload.Name = row.Name || "";
    } else {
      payload.StudentKey = row.StudentKey || null;
      payload.StudentID = row.StudentID || null;
    }

    return payload;
  };

  const validateForm = (cfg, row) => {
    for (const field of cfg.fields) {
      if (field === "Person" || field === "IsActive") continue;
      if (
        ["FromDate", "ToDate"].includes(field)
      ) {
        continue;
      }

      if (
        [
          "SubCampusCode",
          "DegreeCode",
          "BranchCode",
          "SemesterCode",
          "Semester",
          "Section",
          "Block",
          "FloorNumber",
          "RoomNumber",
        ].includes(field) &&
        !row[field]
      ) {
        return `${field.replace(/([A-Z])/g, " $1").trim()} is required.`;
      }
    }

    if (cfg.personType === "employee" && !row.EmployeeID) {
      return "Please select an employee.";
    }
    if (
      cfg.personType !== "employee" &&
      !row.StudentID &&
      !row.StudentKey
    ) {
      return "Please select a student.";
    }

    return "";
  };

  const saveForm = async () => {
    const validation = validateForm(activeConfig, formData);
    if (validation) {
      return setMsg({ type: "error", text: validation });
    }

    setSaving(true);
    try {
      let resolved = { ...formData };

      if (
        activeConfig.personType === "hostelStudent" &&
        !resolved.StudentKey &&
        resolved.StudentID
      ) {
        const key = await resolveHostelStudentKey(
          resolved.StudentID
        );
        if (!key) {
          throw new Error(
            "Unable to resolve StudentKey for selected hostel representative."
          );
        }
        resolved.StudentKey = key;
      }

      const payload = buildPayload(activeConfig, resolved);

      if (formMode === "add") {
        await axios.post(
          `${API_URL}/api/${activeConfig.endpoint}`,
          payload,
          authHeader
        );
      } else {
        await axios.put(
          `${API_URL}/api/${activeConfig.endpoint}/${formData.ID}`,
          payload,
          authHeader
        );
      }

      setMsg({
        type: "success",
        text:
          formMode === "add"
            ? `${activeConfig.label} assignment added successfully.`
            : `${activeConfig.label} assignment updated successfully.`,
      });

      setFormOpen(false);
      await fetchRows(activeConfig, { silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          e.message ||
          `Failed to save ${activeConfig.label} assignment.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (
      !window.confirm(
        `Delete this ${activeConfig.label} assignment?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/${activeConfig.endpoint}/${row.ID}`,
        authHeader
      );

      setSelectedByRole((prev) => ({
        ...prev,
        [activeConfig.key]: (
          prev[activeConfig.key] || []
        ).filter((id) => id !== row.ID),
      }));

      setMsg({
        type: "success",
        text: `${activeConfig.label} assignment deleted successfully.`,
      });

      await fetchRows(activeConfig, { silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          `Failed to delete ${activeConfig.label} assignment.`,
      });
    }
  };

  const selectedRows =
    selectedByRole[activeConfig.key] || [];
  const currentPageIds = paginatedRows.map((row) => row.ID);
  const selectedOnPage = currentPageIds.filter((id) =>
    selectedRows.includes(id)
  );

  const selectCurrentPage = (checked) => {
    setSelectedByRole((prev) => {
      const current = prev[activeConfig.key] || [];

      return {
        ...prev,
        [activeConfig.key]: checked
          ? [...new Set([...current, ...currentPageIds])]
          : current.filter(
              (id) => !currentPageIds.includes(id)
            ),
      };
    });
  };

  const toggleRowSelection = (id) => {
    setSelectedByRole((prev) => {
      const current = prev[activeConfig.key] || [];
      return {
        ...prev,
        [activeConfig.key]: current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id],
      };
    });
  };

  const bulkStatusUpdate = async (isActive) => {
    if (!selectedRows.length) return;

    try {
      if (activeConfig.bulkStatusEndpoint) {
        await axios.put(
          `${API_URL}/api/${activeConfig.bulkStatusEndpoint}`,
          {
            IDs: selectedRows,
            IsActive: isActive ? 1 : 0,
          },
          authHeader
        );
      } else {
        await Promise.all(
          selectedRows.map(async (id) => {
            const row = (dataByRole[activeConfig.key] || []).find(
              (item) => item.ID === id
            );

            if (!row) return;

            await axios.put(
              `${API_URL}/api/${activeConfig.endpoint}/${id}`,
              {
                ...buildPayload(activeConfig, row),
                IsActive: isActive ? 1 : 0,
              },
              authHeader
            );
          })
        );
      }

      setSelectedByRole((prev) => ({
        ...prev,
        [activeConfig.key]: [],
      }));

      setMsg({
        type: "success",
        text: `Selected assignments ${
          isActive ? "activated" : "deactivated"
        } successfully.`,
      });

      await fetchRows(activeConfig, { silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          "Failed to update selected assignments.",
      });
    }
  };

  const downloadSample = (cfg = activeConfig) => {
    const sampleRows = cfg.sampleRows(campusCode);
    const headers = Object.keys(sampleRows[0] || {});
    const ws = XLSX.utils.json_to_sheet(sampleRows, {
      header: headers,
    });
    ws["!cols"] = headers.map((header) => ({
      wch: Math.max(14, header.length + 2),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      cfg.sampleSheet
    );

    const wbout = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${cfg.sampleFile}_${campusCode}.xlsx`
    );
  };

  const bulkUpload = async (file, cfg = activeConfig) => {
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);

    setLoadingRows(true);
    try {
      await axios.post(
        `${API_URL}/api/${cfg.uploadEndpoint}`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMsg({
        type: "success",
        text: `${cfg.label} bulk upload completed successfully.`,
      });

      await fetchRows(cfg, { silent: true });
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          `${cfg.label} bulk upload failed.`,
      });
    } finally {
      setLoadingRows(false);
    }
  };

  const getFormOptions = (field) => {
    if (field === "SubCampusCode") {
      return [
        ...new Set([
          ...cascadeLookup.SubCampusCode,
          ...academicLookup.SubCampusCode,
        ]),
      ];
    }

    if (field === "DegreeCode") {
      return cascadeLookup.DegreeCode.length
        ? cascadeLookup.DegreeCode
        : academicLookup.DegreeCode;
    }

    if (field === "BranchCode") {
      return cascadeLookup.BranchCode.length
        ? cascadeLookup.BranchCode
        : academicLookup.BranchCode;
    }

    if (field === "SemesterCode") {
      return cascadeLookup.SemesterCode.length
        ? cascadeLookup.SemesterCode
        : academicLookup.SemesterCode;
    }

    if (field === "Semester") {
      return cascadeLookup.Semester.length
        ? cascadeLookup.Semester
        : academicLookup.Semester;
    }

    if (field === "Section") {
      return cascadeLookup.Section.length
        ? cascadeLookup.Section
        : academicLookup.Section;
    }

    if (field === "Block") {
      return hostelLookup.blocks;
    }

    if (field === "FloorNumber") {
      const roomFloors = (hostelLookup.rooms || [])
        .filter(
          (room) =>
            !formData.Block ||
            norm(room.Block) === norm(formData.Block)
        )
        .map((room) => String(room.FloorNumber ?? ""))
        .filter(Boolean);

      return [
        ...new Set([...hostelLookup.floors, ...roomFloors]),
      ];
    }

    if (field === "RoomNumber") {
      return [
        ...new Set(
          (hostelLookup.rooms || [])
            .filter((room) => {
              const blockOk =
                !formData.Block ||
                norm(room.Block) === norm(formData.Block);
              const floorOk =
                !formData.FloorNumber ||
                norm(room.FloorNumber) ===
                  norm(formData.FloorNumber);
              return blockOk && floorOk;
            })
            .map((room) => String(room.RoomNumber ?? ""))
            .filter(Boolean)
        ),
      ];
    }

    return [];
  };

  const renderFilter = (field) => {
    if (field === "People") {
      const selected =
        filtersByRole[activeConfig.key]?.People || [];

      return (
        <Autocomplete
          multiple
          options={personOptions}
          value={selected}
          loading={personLoading}
          getOptionLabel={(option) =>
            option?.label || String(option || "")
          }
          isOptionEqualToValue={(a, b) =>
            String(a?.key || a?.id || a?.label) ===
            String(b?.key || b?.id || b?.label)
          }
          onInputChange={(_, value, reason) => {
            if (reason === "input") {
              searchPeople(value, activeConfig);
            }
          }}
          onChange={(_, value) =>
            updateFilter("People", value)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                activeConfig.personType === "employee"
                  ? "Search People"
                  : "Search Students"
              }
              size="small"
              sx={inputSx}
              placeholder="Type at least 2 letters"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {personLoading ? (
                      <CircularProgress
                        color="inherit"
                        size={18}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    const options = getFilterOptions(field);
    const selected =
      filtersByRole[activeConfig.key]?.[field] || [];

    const labels = {
      SubCampusCode: "Sub Campus",
      DegreeCode: "Degree",
      BranchCode: "Branch",
      SemesterCode: "Semester",
      Semester: "Semester",
      Section: "Section",
      Block: "Block",
      FloorNumber: "Floor",
      RoomNumber: "Room",
    };

    return (
      <Autocomplete
        multiple
        options={options}
        value={selected}
        onChange={(_, value) => updateFilter(field, value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={labels[field] || field}
            size="small"
            sx={inputSx}
          />
        )}
      />
    );
  };

  const renderFormField = (field) => {
    if (field === "Person") {
      let selected = null;

      if (activeConfig.personType === "employee") {
        selected =
          personOptions.find(
            (person) =>
              String(person.id) ===
              String(formData.EmployeeID || "")
          ) || null;
      } else {
        selected =
          personOptions.find(
            (person) =>
              String(person.studentId || person.id) ===
                String(formData.StudentID || "") ||
              String(person.studentKey || "") ===
                String(formData.StudentKey || "")
          ) || null;
      }

      return (
        <Autocomplete
          options={personOptions}
          value={selected}
          loading={personLoading}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(a, b) =>
            String(a?.key || a?.id) ===
            String(b?.key || b?.id)
          }
          onInputChange={(_, value, reason) => {
            if (reason === "input") {
              searchPeople(value, activeConfig);
            }
          }}
          onChange={(_, person) => {
            if (!person) {
              setFormData((prev) => ({
                ...prev,
                EmployeeID:
                  activeConfig.personType === "employee"
                    ? null
                    : prev.EmployeeID,
                StudentID:
                  activeConfig.personType !== "employee"
                    ? ""
                    : prev.StudentID,
                StudentKey:
                  activeConfig.personType !== "employee"
                    ? null
                    : prev.StudentKey,
                [activeConfig.personNameField]: "",
              }));
              return;
            }

            setFormData((prev) => {
              if (activeConfig.personType === "employee") {
                return {
                  ...prev,
                  EmployeeID: person.id,
                  Name: person.name || "",
                };
              }

              if (activeConfig.personType === "student") {
                return {
                  ...prev,
                  StudentID:
                    person.studentId || person.id || "",
                  StudentKey:
                    person.studentKey || person.key || null,
                  Name: person.name || "",
                };
              }

              return {
                ...prev,
                StudentID:
                  person.studentId || person.id || "",
                StudentKey:
                  person.studentKey || null,
                RepresentativeName: person.name || "",
              };
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                activeConfig.personType === "employee"
                  ? "Employee"
                  : "Student"
              }
              size="small"
              sx={inputSx}
              placeholder="Type at least 2 letters"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {personLoading ? (
                      <CircularProgress
                        color="inherit"
                        size={18}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      );
    }

    if (field === "FromDate" || field === "ToDate") {
      return (
        <DatePicker
          label={field === "FromDate" ? "From Date" : "To Date"}
          value={
            formData[field] ? dayjs(formData[field]) : null
          }
          onChange={(value) =>
            updateFormField(
              field,
              value ? value.format("YYYY-MM-DD") : ""
            )
          }
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              sx: inputSx,
            },
          }}
        />
      );
    }

    if (field === "IsActive") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: 40,
          }}
        >
          <Checkbox
            checked={Boolean(formData.IsActive)}
            onChange={(e) =>
              updateFormField(
                "IsActive",
                e.target.checked ? 1 : 0
              )
            }
            sx={checkboxSx}
          />
          <Typography variant="body2">Active</Typography>
        </Box>
      );
    }

    const labels = {
      SubCampusCode: "Sub Campus",
      DegreeCode: "Degree",
      BranchCode: "Branch",
      SemesterCode: "Semester",
      Semester: "Semester",
      Section: "Section",
      Block: "Block",
      FloorNumber: "Floor",
      RoomNumber: "Room",
    };

    const options = getFormOptions(field);

    return (
      <Autocomplete
        options={options}
        value={
          formData[field] !== undefined &&
          formData[field] !== null &&
          String(formData[field]) !== ""
            ? String(formData[field])
            : null
        }
        onChange={(_, value) =>
          updateFormField(field, value || "")
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={labels[field] || field}
            size="small"
            sx={inputSx}
          />
        )}
      />
    );
  };

  const renderActiveCheckbox = (row) => (
    <Checkbox
      checked={Number(row.IsActive) === 1}
      disabled
      sx={{
        p: 0.5,
        color: BLUE,
        "&.Mui-checked": { color: THEME_ORANGE },
        "&.Mui-disabled": {
          color:
            Number(row.IsActive) === 1
              ? `${THEME_ORANGE} !important`
              : "rgba(25,118,210,0.35) !important",
        },
      }}
    />
  );

  const columnLabels = {
    SubCampusCode: "Sub Campus",
    DegreeCode: "Degree",
    BranchCode: "Branch",
    SemesterCode: "Semester",
    Semester: "Semester",
    Section: "Section",
    Block: "Block",
    FloorNumber: "Floor",
    RoomNumber: "Room",
    Name: "Name",
    RepresentativeName: "Representative",
    EmployeeID: "Employee ID",
    StudentID: "Student ID",
    FromDate: "From Date",
    ToDate: "To Date",
    IsActive: "Active",
  };

  const renderTable = () => (
    <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
      <Box sx={{ overflowX: "auto" }}>
        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: Math.max(
                920,
                200 + activeConfig.columns.length * 135
              ),
            }}
          >
            <TableHead>
              <TableRow sx={theadRowSx}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      currentPageIds.length > 0 &&
                      selectedOnPage.length ===
                        currentPageIds.length
                    }
                    indeterminate={
                      selectedOnPage.length > 0 &&
                      selectedOnPage.length <
                        currentPageIds.length
                    }
                    onChange={(e) =>
                      selectCurrentPage(e.target.checked)
                    }
                    sx={{
                      color: "rgba(255,255,255,.8)",
                      "&.Mui-checked": { color: "white" },
                      "&.MuiCheckbox-indeterminate": {
                        color: "white",
                      },
                    }}
                  />
                </TableCell>
                <TableCell>#</TableCell>
                {activeConfig.columns.map((column) => (
                  <TableCell key={column}>
                    {columnLabels[column] || column}
                  </TableCell>
                ))}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.map((row, index) => {
                const serial =
                  safePage * currentRowsPerPage +
                  index +
                  1;

                return (
                  <TableRow
                    key={row.ID ?? `${activeConfig.key}-${index}`}
                    sx={zebraRowSx(index)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRows.includes(row.ID)}
                        onChange={() =>
                          toggleRowSelection(row.ID)
                        }
                        sx={checkboxSx}
                      />
                    </TableCell>

                    <TableCell>{serialBadge(serial)}</TableCell>

                    {activeConfig.columns.map((column) => (
                      <TableCell key={column}>
                        {column === "IsActive"
                          ? renderActiveCheckbox(row)
                          : row[column] || "—"}
                      </TableCell>
                    ))}

                    <TableCell
                      align="center"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          sx={iconBtnSx}
                          onClick={() => openEditForm(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          sx={dangerIconBtnSx}
                          onClick={() => deleteRow(row)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {!loadingRows && !paginatedRows.length && (
                <TableRow>
                  <TableCell
                    colSpan={activeConfig.columns.length + 4}
                  >
                    <Typography
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      No {activeConfig.label} assignments found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              <TableRow>
                <TableCell
                  colSpan={activeConfig.columns.length + 4}
                  sx={{
                    p: 0,
                    borderBottom: 0,
                    backgroundColor:
                      "rgba(25,118,210,.025)",
                  }}
                >
                  <Button
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={openAddForm}
                    sx={{
                      justifyContent: "center",
                      py: 1.35,
                      borderRadius: 0,
                      textTransform: "none",
                      fontWeight: 900,
                      color: THEME_ORANGE,
                      borderTop: `1px solid ${BORDER}`,
                      backgroundColor:
                        "rgba(240,120,40,.035)",
                      "&:hover": {
                        backgroundColor:
                          "rgba(240,120,40,.085)",
                      },
                    }}
                  >
                    Add {activeConfig.label}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {loadingRows && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 2,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={safePage}
        onPageChange={(_, page) =>
          setPageByRole((prev) => ({
            ...prev,
            [activeConfig.key]: page,
          }))
        }
        rowsPerPage={currentRowsPerPage}
        onRowsPerPageChange={(e) => {
          const size = parseInt(e.target.value, 10);
          setRowsPerPageByRole((prev) => ({
            ...prev,
            [activeConfig.key]: size,
          }));
          setPageByRole((prev) => ({
            ...prev,
            [activeConfig.key]: 0,
          }));
        }}
        rowsPerPageOptions={[
          10, 25, 50, 100, 200, 300,
        ]}
        labelRowsPerPage="Rows per page"
        showFirstButton
        showLastButton
      />
    </Paper>
  );

  const renderFilters = () => (
    <Paper elevation={0} sx={sectionSx}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, color: BLUE }}>
            Filters
          </Typography>
          <Typography
            sx={{
              fontSize: ".82rem",
              color: "text.secondary",
            }}
          >
            Narrow the {activeConfig.label} assignments shown below.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<CloseIcon />}
          onClick={clearFilters}
          sx={outlinedBtnSx}
        >
          Clear
        </Button>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,minmax(220px,1fr))",
            lg: `repeat(${Math.min(
              4,
              Math.max(1, activeConfig.filters.length)
            )},minmax(220px,1fr))`,
          },
          gap: 1.5,
        }}
      >
        {activeConfig.filters.map((filter) => (
          <Box key={filter}>{renderFilter(filter)}</Box>
        ))}
      </Box>
    </Paper>
  );

  const formDialog = (
    <Dialog
      open={formOpen}
      onClose={() => !saving && setFormOpen(false)}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${BORDER}`,
          boxShadow:
            "0 18px 55px rgba(15,23,42,.22)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            ...headerSx,
            px: 3,
            py: 2,
          }}
        >
          <Typography sx={{ fontWeight: 900 }}>
            {formMode === "add" ? "Add" : "Edit"}{" "}
            {activeConfig.label} Assignment
          </Typography>
          <Typography
            sx={{
              fontSize: ".82rem",
              opacity: .9,
            }}
          >
            Configure assignment scope, person, dates and active status.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: BG,
          p: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          elevation={0}
          sx={{ ...popupSectionSx, mt: 2 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2,minmax(220px,1fr))",
                lg: "repeat(3,minmax(220px,1fr))",
              },
              gap: 2,
            }}
          >
            <TextField
              label="Campus"
              value={campusCode}
              disabled
              size="small"
              sx={inputSx}
            />

            {activeConfig.fields.map((field) => (
              <Box key={field}>
                {renderFormField(field)}
              </Box>
            ))}
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Button
          onClick={() => setFormOpen(false)}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={saveForm}
          disabled={saving}
          sx={primaryBtnSx}
        >
          {saving
            ? "Saving..."
            : formMode === "add"
            ? `Add ${activeConfig.label}`
            : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageWrapSx}>
        <Paper elevation={5} sx={rootPaperSx}>
          <Box
            sx={{
              ...headerSx,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                Role Assignment
              </Typography>

              <Typography
                sx={{
                  opacity: .9,
                  fontSize: ".85rem",
                  mt: .25,
                }}
              >
                Assign and manage academic and hostel roles from one page.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAddForm}
                sx={{
                  ...primaryBtnSx,
                  backgroundColor: "white",
                  color: THEME_ORANGE,
                  "&:hover": {
                    backgroundColor:
                      "rgba(255,255,255,.92)",
                  },
                }}
              >
                Add {activeConfig.label}
              </Button>

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadSample(activeConfig)}
                sx={outlinedBtnSx}
              >
                Download Sample
              </Button>

              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() =>
                  fileInputs.current[
                    activeConfig.key
                  ]?.click()
                }
                sx={primaryBtnSx}
              >
                Bulk Upload
              </Button>

              <input
                hidden
                type="file"
                accept=".xlsx,.xls"
                ref={(node) => {
                  fileInputs.current[activeConfig.key] =
                    node;
                }}
                onChange={(e) => {
                  bulkUpload(
                    e.target.files?.[0],
                    activeConfig
                  );
                  e.target.value = "";
                }}
              />

              {selectedRows.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => bulkStatusUpdate(true)}
                    sx={{
                      ...outlinedBtnSx,
                      color: "white",
                      borderColor:
                        "rgba(255,255,255,.65)",
                      backgroundColor:
                        "rgba(255,255,255,.12)",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor:
                          "rgba(255,255,255,.20)",
                      },
                    }}
                  >
                    Activate ({selectedRows.length})
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => bulkStatusUpdate(false)}
                    sx={{
                      ...outlinedBtnSx,
                      color: "white",
                      borderColor:
                        "rgba(255,255,255,.65)",
                      backgroundColor:
                        "rgba(255,255,255,.12)",
                      "&:hover": {
                        borderColor: "white",
                        backgroundColor:
                          "rgba(255,255,255,.20)",
                      },
                    }}
                  >
                    Deactivate ({selectedRows.length})
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ p: 2, bgcolor: BG }}>
            <Paper elevation={0} sx={surfaceSx}>
              <Tabs
                value={tab}
                onChange={(_, value) => {
                  setTab(value);
                  setFormOpen(false);
                  setPersonOptions([]);
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={tabsSx}
              >
                {ROLE_CONFIGS.map((cfg) => (
                  <Tab
                    key={cfg.key}
                    label={cfg.label}
                    icon={cfg.icon}
                    iconPosition="start"
                  />
                ))}
              </Tabs>

              <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Paper elevation={0} sx={sectionSx}>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: BLUE,
                      }}
                    >
                      {activeConfig.title} Assignments
                    </Typography>

                    <Typography
                      sx={{
                        mt: .25,
                        fontSize: ".85rem",
                        color: "text.secondary",
                      }}
                    >
                      {activeConfig.description}
                    </Typography>
                  </Paper>

                  {renderFilters()}
                  {renderTable()}
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Paper>

        {formDialog}

        <Snackbar
          open={Boolean(msg.text)}
          autoHideDuration={3500}
          onClose={() =>
            setMsg((prev) => ({
              ...prev,
              text: "",
            }))
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            severity={msg.type}
            onClose={() =>
              setMsg((prev) => ({
                ...prev,
                text: "",
              }))
            }
            sx={{ borderRadius: 2 }}
          >
            {msg.text}
          </Alert>
        </Snackbar>

        {loadingLookups && (
          <Box
            sx={{
              position: "fixed",
              right: 24,
              bottom: 24,
              zIndex: 1500,
              backgroundColor: "white",
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              px: 1.5,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              boxShadow:
                "0 8px 24px rgba(15,23,42,.12)",
            }}
          >
            <CircularProgress size={18} />
            <Typography
              sx={{
                fontSize: ".8rem",
                color: "text.secondary",
              }}
            >
              Loading lookups...
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}
